import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { referansAPI, caseFileAPI, masrafAPI } from '../api/api';
import { davaTuruEtiketi } from '../utils/constants';
import ColumnControl from '../components/Common/ColumnControl';
import './ReferansCaseFiles.css';

const ReferansCaseFiles = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [referans, setReferans] = useState(null);
  const [caseFiles, setCaseFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [masraflar, setMasraflar] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // 1-12
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');

  const isAdmin = () => {
    if (!user) return false;
    const role = user.Role ?? user.role ?? '';
    return role === 'Admin';
  };
  
  // Column visibility control
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('referansCaseFiles_visibleColumns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Ensure all columns are in the saved state, including dosyaGeliri
        const defaultColumns = {
          foyNo: true,
          muvekkil: true,
          dosyaTuru: true,
          dosyaGelisTarihi: true,
          avans: true,
          dosyaGeliri: true
        };
        // Merge saved with defaults to ensure all columns exist
        return { ...defaultColumns, ...parsed };
      } catch {
        return {
          foyNo: true,
          muvekkil: true,
          dosyaTuru: true,
          dosyaGelisTarihi: true,
          avans: true,
          dosyaGeliri: true
        };
      }
    }
    // Default: all columns visible
    return {
      foyNo: true,
      muvekkil: true,
      dosyaTuru: true,
      dosyaGelisTarihi: true,
      avans: true,
      dosyaGeliri: true
    };
  });

  // Admin olmayan kullanıcılar için "Avans" ve "Dosya Geliri" sütunlarını gizle
  const columns = [
    { key: 'foyNo', label: 'Föy No' },
    { key: 'muvekkil', label: 'Müvekkil' },
    { key: 'dosyaTuru', label: 'Dosya Türü' },
    { key: 'dosyaGelisTarihi', label: 'Dosyanın Geliş Tarihi' },
    ...(isAdmin() ? [
      { key: 'avans', label: 'Avans' },
      { key: 'dosyaGeliri', label: 'Dosya Geliri' }
    ] : [])
  ];

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns(prev => {
      const newState = { ...prev, [columnKey]: !prev[columnKey] };
      localStorage.setItem('referansCaseFiles_visibleColumns', JSON.stringify(newState));
      return newState;
    });
  };

  // Arama ile filtrelenmiş dosya listesi
  const filteredCaseFiles = useMemo(() => {
    if (!searchTerm.trim()) return caseFiles;
    const s = searchTerm.toLowerCase().trim();
    return caseFiles.filter(file => {
      const foyNo = String(file.foyNo ?? file.FoyNo ?? '').toLowerCase();
      const muvekkil = String(file.muvekkil ?? file.Muvekkil ?? '').toLowerCase();
      const dosyaTuru = (davaTuruEtiketi(file) || '-').toLowerCase();
      return foyNo.includes(s) || muvekkil.includes(s) || dosyaTuru.includes(s);
    });
  }, [caseFiles, searchTerm]);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [referansData, filesData] = await Promise.all([
        referansAPI.getById(id),
        referansAPI.getCaseFilesByReferans(id)
      ]);
      setReferans(referansData);
      setCaseFiles(filesData);
      
      // Tüm masrafları yükle
      try {
        const allMasraflar = await masrafAPI.getAll();
        setMasraflar(allMasraflar || []);
        
      } catch (error) {
        console.error('Masraflar yüklenirken hata:', error);
      }
    } catch (error) {
      console.error('Veri yüklenirken hata:', error);
      alert('Veri yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCaseFileClick = (caseFileId) => {
    navigate(`/case-files/${caseFileId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getDavaTuruLabel = (dosya) => davaTuruEtiketi(dosya) || '-';

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === 0) return '₺0,00';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  // Dosya için avans hesapla (bu referans için)
  const getAvansForFile = (fileId) => {
    const referansId = parseInt(id);
    const avansMasraflari = masraflar.filter(m => {
      const masrafTuru = m.masrafTuru || m.MasrafTuru || '';
      const masrafTuruLower = masrafTuru.toLowerCase().trim();
      const isReferansAvans = masrafTuruLower === 'referansa verilen avans' || 
                             masrafTuru === 'REFERANSA VERİLEN AVANS' ||
                             masrafTuru === 'Referansa Verilen Avans';
      const masrafReferansId = m.referansId || m.ReferansId;
      const masrafCaseFileId = m.caseFileId || m.CaseFileId;
      
      const masrafCaseFileIdInt = masrafCaseFileId ? parseInt(masrafCaseFileId) : null;
      const fileIdInt = parseInt(fileId);
      const masrafReferansIdInt = masrafReferansId ? parseInt(masrafReferansId) : null;
      
      return masrafCaseFileIdInt === fileIdInt &&
             isReferansAvans &&
             masrafReferansIdInt === referansId;
    });
    return avansMasraflari.reduce((sum, m) => sum + (parseFloat(m.tutar || m.Tutar) || 0), 0);
  };

  // Dosya için net gelir hesapla
  const getDosyaGeliri = (fileId) => {
    const fileMasraflari = masraflar.filter(m => (m.caseFileId || m.CaseFileId) === fileId);
    const yeniMasraflar = fileMasraflari.filter(m => m.MasrafTuru || m.masrafTuru);
    const gelirler = yeniMasraflar.filter(m => m.IsGelir || m.isGelir);
    const giderler = yeniMasraflar.filter(m => !(m.IsGelir || m.isGelir));
    const toplamGelir = gelirler.reduce((sum, m) => sum + (parseFloat(m.Tutar || m.tutar) || 0), 0);
    const toplamGider = giderler.reduce((sum, m) => sum + (parseFloat(m.Tutar || m.tutar) || 0), 0);
    return toplamGelir - toplamGider;
  };

  // Seçilen ay için referansa verilen toplam avans
  const getToplamAvans = () => {
    const referansId = parseInt(id);
    
    const avansMasraflari = masraflar.filter(m => {
      const masrafTarih = m.tarih || m.Tarih || m.MasrafIstenmeTarihi || m.masrafIstenmeTarihi;
      if (!masrafTarih) return false;
      const tarih = new Date(masrafTarih);
      const masrafTuru = m.masrafTuru || m.MasrafTuru || '';
      const masrafTuruLower = masrafTuru.toLowerCase().trim();
      const isReferansAvans = masrafTuruLower === 'referansa verilen avans' || 
                             masrafTuru === 'REFERANSA VERİLEN AVANS' ||
                             masrafTuru === 'Referansa Verilen Avans';
      const masrafReferansId = m.referansId || m.ReferansId;
      const masrafReferansIdInt = masrafReferansId ? parseInt(masrafReferansId) : null;
      return isReferansAvans &&
             masrafReferansIdInt === referansId &&
             tarih.getMonth() === selectedMonth - 1 && // JavaScript'te ay 0-11 arası
             tarih.getFullYear() === selectedYear;
    });
    
    return avansMasraflari.reduce((sum, m) => sum + (parseFloat(m.tutar || m.Tutar) || 0), 0);
  };

  // Tüm zamanlar için referansa verilen toplam avans
  const getToplamAvansTumZamanlar = () => {
    const referansId = parseInt(id);
    
    const avansMasraflari = masraflar.filter(m => {
      const masrafTuru = m.masrafTuru || m.MasrafTuru || '';
      const masrafTuruLower = masrafTuru.toLowerCase().trim();
      const isReferansAvans = masrafTuruLower === 'referansa verilen avans' || 
                             masrafTuru === 'REFERANSA VERİLEN AVANS' ||
                             masrafTuru === 'Referansa Verilen Avans';
      const masrafReferansId = m.referansId || m.ReferansId;
      const masrafReferansIdInt = masrafReferansId ? parseInt(masrafReferansId) : null;
      return isReferansAvans && masrafReferansIdInt === referansId;
    });
    
    return avansMasraflari.reduce((sum, m) => sum + (parseFloat(m.tutar || m.Tutar) || 0), 0);
  };

  // Ay isimleri
  const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  // Yıl seçenekleri (son 5 yıl + gelecek 1 yıl)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      years.push(i);
    }
    return years;
  };

  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }

  if (!referans) {
    return <div className="error">Referans bulunamadı.</div>;
  }

  return (
    <div className="referans-casefiles-container">
      <div className="page-header">
        <button className="btn btn-secondary" onClick={() => navigate('/referanslar')}>
          ← Geri
        </button>
        <h1>{referans.name} - Dosyalar</h1>
        {referans.description && (
          <p className="referans-description">{referans.description}</p>
        )}
      </div>

      <div className="casefiles-table-container">
        {caseFiles.length > 0 && (
          <>
            <div className="referans-casefiles-search-row">
              <input
                type="text"
                className="referans-casefiles-search-input"
                placeholder="Föy No, müvekkil veya dosya türü ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
              />
              <button
                type="button"
                className="btn btn-primary referans-casefiles-search-btn"
                onClick={() => document.querySelector('.referans-casefiles-search-input')?.focus()}
              >
                Ara
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
              <ColumnControl
                columns={columns}
                visibleColumns={visibleColumns}
                onToggleColumn={handleToggleColumn}
              />
            </div>
          </>
        )}
        {caseFiles.length === 0 ? (
          <div className="no-data">Bu referansa ait dosya bulunmamaktadır.</div>
        ) : filteredCaseFiles.length === 0 ? (
          <div className="no-data">Aramanızla eşleşen dosya bulunamadı.</div>
        ) : (
          <table className="casefiles-table">
              <thead>
                <tr>
                  {visibleColumns.foyNo && <th>Föy No</th>}
                  {visibleColumns.muvekkil && <th>Müvekkil</th>}
                  {visibleColumns.dosyaTuru && <th>Dosya Türü</th>}
                  {visibleColumns.dosyaGelisTarihi && <th>Dosyanın Geliş Tarihi</th>}
                  {isAdmin() && visibleColumns.avans && <th>Avans</th>}
                  {isAdmin() && visibleColumns.dosyaGeliri && <th>Dosya Geliri</th>}
                </tr>
              </thead>
              <tbody>
                {filteredCaseFiles.map((file) => (
                  <tr 
                    key={file.id}
                    onClick={() => handleCaseFileClick(file.id)}
                    className="casefile-row"
                  >
                    {visibleColumns.foyNo && <td>{file.foyNo || file.FoyNo || '-'}</td>}
                    {visibleColumns.muvekkil && <td>{file.muvekkil || file.Muvekkil || '-'}</td>}
                    {visibleColumns.dosyaTuru && <td>{getDavaTuruLabel(file)}</td>}
                    {visibleColumns.dosyaGelisTarihi && <td>{formatDate(file.dosyaGelisTarihi || file.DosyaGelisTarihi)}</td>}
                    {isAdmin() && visibleColumns.avans && <td>{formatCurrency(getAvansForFile(file.id))}</td>}
                    {isAdmin() && visibleColumns.dosyaGeliri && <td>{formatCurrency(getDosyaGeliri(file.id))}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
        )}
      </div>

      {/* Toplam Avans Bilgisi - Sadece Admin için */}
      {isAdmin() && caseFiles.length > 0 && (
        <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
            <h3 style={{ margin: 0, color: '#2c3e50' }}>Referansa Verilen Toplam Avans</h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#495057' }}>
                <span>Ay:</span>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    fontSize: '14px',
                    cursor: 'pointer',
                    backgroundColor: 'white'
                  }}
                >
                  {monthNames.map((month, index) => (
                    <option key={index + 1} value={index + 1}>
                      {month}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#495057' }}>
                <span>Yıl:</span>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border: '1px solid #ced4da',
                    fontSize: '14px',
                    cursor: 'pointer',
                    backgroundColor: 'white'
                  }}
                >
                  {getYearOptions().map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
              <button
                onClick={() => {
                  const now = new Date();
                  setSelectedMonth(now.getMonth() + 1);
                  setSelectedYear(now.getFullYear());
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: '4px',
                  border: '1px solid #007bff',
                  backgroundColor: '#007bff',
                  color: 'white',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Bu Ay
              </button>
            </div>
          </div>
          <p style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
            {formatCurrency(getToplamAvans())}
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
            {monthNames[selectedMonth - 1]} {selectedYear} için toplam avans
          </p>
        </div>
      )}

      {/* Tüm Zamanlar Toplam Avans Bilgisi - Sadece Admin için */}
      {isAdmin() && caseFiles.length > 0 && (
        <div style={{ marginTop: '24px', padding: '20px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '2px solid #007bff' }}>
          <h3 style={{ margin: '0 0 12px 0', color: '#0056b3', fontSize: '20px', fontWeight: '600' }}>
            Tüm Zamanlar Referansa Ödenen Toplam Ücret
          </h3>
          <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', color: '#0056b3' }}>
            {formatCurrency(getToplamAvansTumZamanlar())}
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#495057' }}>
            Bu referansa tüm zamanlar boyunca ödenen toplam avans tutarı
          </p>
        </div>
      )}
    </div>
  );
};

export default ReferansCaseFiles;

